/**
 * The rAF cadence estimator: which refresh rate were these callback gaps
 * delivered at?
 *
 * Why not read the display. No web API exposes a refresh rate, and the native
 * one (`NSScreen.maximumFramesPerSecond`, `dmDisplayFrequency`) answers the
 * wrong question: WebKit can hold rAF at 60 Hz on a 120 Hz ProMotion panel and
 * drops it to 30 Hz under Low Power Mode or thermal pressure, while the panel
 * still reports 120. What normalisation needs is the cadence the page was
 * actually given, and only the gaps themselves carry that.
 *
 * Why not the shortest gap. The minimum of N jittered samples lands in the
 * low tail of the jitter, not on the frame period: on a 120 Hz panel it read
 * ~7.4 ms, i.e. 135 Hz, which is a rate no display runs at.
 *
 * Why not a low quantile on its own either. A WebKit rAF timestamp marks when
 * the main thread began the rendering update, not the vsync, so every
 * timestamp carries that frame's queueing delay and a gap is
 * `period + delay[k] - delay[k-1]`: a late frame followed by an on-time one
 * reads short. That jitter is symmetric about the period, so any quantile
 * below the median sits below the period by a margin that grows with it — and
 * the snap boundaries are narrow (60|72 is 15.21 ms, 1.5 ms under a 60 Hz
 * frame). The p25 alone read a smooth 60 Hz desktop webview as 72 or 75 Hz
 * once the mean delay passed ~2 ms, and a 120 Hz panel as 144 at ~1 ms.
 *
 * So two steps. The p25 only LOCATES the single-frame cluster: it is above the
 * jitter's low tail and below the long gaps a janky episode adds, as long as a
 * quarter of the intervals were single frames. The period is then the median
 * of the gaps within `CADENCE_BAND` of it, where symmetric jitter cancels; the
 * band keeps out the doubled and frozen gaps a median over everything would
 * be dragged up by. Snapping to the nearest standard rate absorbs what is
 * left, so a short episode still reads `120`, not `125`.
 *
 * What it cannot tell apart: a 60 Hz episode that dropped three frames in
 * every four and a webview throttled to 30 Hz produce the same gaps. The
 * minimum sample count keeps a handful of callbacks from guessing at all; the
 * rest is read at the dashboard, where a device's cadence is stable across
 * episodes and a one-off `30` is visible as the outlier it is.
 */

/** Refresh rates panels and throttled webviews actually run at. */
export const STANDARD_REFRESH_RATES_HZ: readonly number[] = [
  24, 30, 48, 50, 60, 72, 75, 90, 100, 120, 144, 165, 180, 240,
];

/** Below this many intervals the estimate is `0` (unknown), not a guess. */
export const MIN_CADENCE_SAMPLES = 8;

/** The quantile of the sorted gaps that locates the single-frame cluster. */
export const CADENCE_QUANTILE = 0.25;

/**
 * The gaps counted as single frames, as multiples of the `CADENCE_QUANTILE`
 * gap. The upper bound sits between two constraints: under the doubled gaps
 * (2× the period) when the locator is AT the period, as it is on a janky
 * episode, and well above the period when jitter has pulled the locator down
 * to ~0.75 of it, or the cluster's upper half is cut and the median drifts
 * low. 1.6 meets both (1.6× and 1.2× the period). The lower bound only drops
 * stray near-zero gaps.
 */
export const CADENCE_BAND: readonly [low: number, high: number] = [0.5, 1.6];

/** The nearest standard rate to `hz`, by ratio so 72/75 and 144/165 split fairly. */
export function snapRefreshRate(hz: number): number {
  let best = STANDARD_REFRESH_RATES_HZ[0]!;
  let bestError = Infinity;
  for (const rate of STANDARD_REFRESH_RATES_HZ) {
    const error = Math.abs(Math.log(hz / rate));
    if (error < bestError) {
      best = rate;
      bestError = error;
    }
  }
  return best;
}

/**
 * The snapped cadence of the first `count` gaps in `gaps`, or `0` when there
 * are too few to say. Copies before sorting, so the caller's buffer (the
 * sampler's preallocated one, the live strip's ring) is left untouched; it
 * runs once per episode close or panel paint, never per frame.
 */
export function estimateCadenceHz(gaps: ArrayLike<number>, count: number): number {
  const valid: number[] = [];
  for (let i = 0; i < count; i++) {
    const gap = gaps[i] ?? 0;
    if (gap > 0) valid.push(gap);
  }
  if (valid.length < MIN_CADENCE_SAMPLES) return 0;
  valid.sort((a, b) => a - b);
  // Step one: locate the single-frame cluster. Biased low by timestamp jitter.
  const locator = valid[Math.floor((valid.length - 1) * CADENCE_QUANTILE)]!;
  // Step two: the median of that cluster, where the jitter cancels. The band
  // always contains the locator itself, so it is never empty.
  const [low, high] = CADENCE_BAND;
  let first = 0;
  while (valid[first]! < locator * low) first += 1;
  let last = valid.length - 1;
  while (valid[last]! > locator * high) last -= 1;
  const period = valid[first + Math.floor((last - first) / 2)]!;
  return snapRefreshRate(1000 / period);
}
